import { useMemo, useState } from "react";
import { ExternalLink, Loader2, X } from "lucide-react";
import { isValidWallet } from "@hupper/shared";
import { formatUSDC, parseUSDC, useGamesActions } from "../hooks/useGames";
import { ACTIVE_CHAIN } from "../contracts/chains";

interface Props {
  walletAddress: string | null;
  usdcBalance: bigint;
  onClose: () => void;
  onSent: () => void;
}

/**
 * SendUsdcModal — straight USDC.transfer from the user's wallet.
 *
 * Polymarket-style: there is no Hupper-held balance to withdraw from. The
 * user's wallet IS the balance. "Send" is just an ERC20 transfer to any
 * address. Signed by Privy embedded wallet (silent if the user enabled
 * the session signer; one prompt otherwise).
 */
export default function SendUsdcModal({ walletAddress, usdcBalance, onClose, onSent }: Props) {
  const [destination, setDestination] = useState("");
  const [amount, setAmount] = useState("");
  const [stage, setStage] = useState<"idle" | "sending" | "success">("idle");

  const { sendUsdc, status } = useGamesActions();

  const destinationValid = useMemo(() => {
    if (!destination.trim()) return null;
    return isValidWallet(destination.trim());
  }, [destination]);

  const parsedAmount = useMemo(() => {
    if (!amount.trim()) return null;
    try {
      return parseUSDC(amount);
    } catch {
      return null;
    }
  }, [amount]);

  const exceedsBalance = parsedAmount !== null && parsedAmount > usdcBalance;

  const canSend =
    destinationValid === true &&
    parsedAmount !== null &&
    parsedAmount > 0n &&
    !exceedsBalance &&
    stage === "idle";

  const handleSend = async () => {
    if (!canSend || !parsedAmount) return;
    setStage("sending");
    try {
      await sendUsdc(destination.trim(), parsedAmount);
      setStage("success");
      onSent();
    } catch (err) {
      setStage("idle");
      // status.error already populated by the hook
      // eslint-disable-next-line no-console
      console.error("[SendUsdcModal] send failed", err);
    }
  };

  const txUrl =
    status.txHash && ACTIVE_CHAIN.explorerUrl
      ? `${ACTIVE_CHAIN.explorerUrl}/tx/${status.txHash}`
      : null;

  return (
    <div
      role="dialog"
      aria-label="Send USDC"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
    >
      <div className="w-full max-w-md bg-[#111] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-[#FF6B00] to-[#F54D12]" />

        <div className="p-5 flex items-center justify-between border-b border-white/5">
          <div className="flex flex-col leading-tight">
            <span className="text-[11px] uppercase tracking-[0.14em] text-white/40 font-bold">
              Send
            </span>
            <span className="text-sm text-white font-semibold">USDC from your wallet</span>
          </div>
          <button
            onClick={onClose}
            aria-label="Close send modal"
            className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/50 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {stage === "success" ? (
            <>
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 text-emerald-200">
                <div className="text-[11px] uppercase tracking-[0.14em] font-bold mb-1">
                  Sent
                </div>
                <div className="text-sm">USDC transfer confirmed on-chain.</div>
                {txUrl && (
                  <a
                    href={txUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-[12px] text-emerald-300 hover:text-emerald-200 underline"
                  >
                    View transaction <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
              <button
                onClick={onClose}
                className="w-full py-2.5 rounded-lg bg-white/10 hover:bg-white/15 text-white font-semibold text-[13px]"
              >
                Close
              </button>
            </>
          ) : (
            <>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.14em] text-white/40 font-bold mb-2">
                  Destination address
                </label>
                <input
                  type="text"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  placeholder="0x..."
                  className={`w-full px-3 py-2.5 bg-[#0A0A0A] border rounded-lg font-mono text-[13px] text-white outline-none transition-colors ${
                    destinationValid === false
                      ? "border-red-500/50 focus:border-red-500"
                      : "border-white/10 focus:border-[#FF6B00]"
                  }`}
                />
                {destinationValid === false && (
                  <div className="mt-1 text-[11px] text-red-400">Not a valid address</div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-[10px] uppercase tracking-[0.14em] text-white/40 font-bold">
                    Amount (USDC)
                  </label>
                  <button
                    onClick={() => setAmount(formatUSDC(usdcBalance))}
                    className="text-[10px] uppercase tracking-[0.14em] text-[#FF6B00] hover:text-[#F54D12] font-bold"
                  >
                    Max ${formatUSDC(usdcBalance)}
                  </button>
                </div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className={`w-full px-3 py-2.5 bg-[#0A0A0A] border rounded-lg font-mono text-[14px] text-white outline-none transition-colors ${
                    exceedsBalance
                      ? "border-red-500/50 focus:border-red-500"
                      : "border-white/10 focus:border-[#FF6B00]"
                  }`}
                />
                {exceedsBalance && (
                  <div className="mt-1 text-[11px] text-red-400">
                    Exceeds your balance (${formatUSDC(usdcBalance)})
                  </div>
                )}
              </div>

              {status.error && (
                <div className="text-[11px] text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-2.5">
                  {status.error.message}
                </div>
              )}

              <button
                onClick={handleSend}
                disabled={!canSend}
                className="w-full py-3 rounded-lg bg-[#FF6B00] hover:bg-[#F54D12] disabled:opacity-30 disabled:cursor-not-allowed text-black font-bold text-[13px] tracking-tight transition-colors inline-flex items-center justify-center gap-2"
              >
                {stage === "sending" || status.pending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  "Send USDC"
                )}
              </button>

              <div className="text-[10px] text-white/30 leading-relaxed text-center">
                Sent straight from {walletAddress ? `${walletAddress.slice(0, 6)}…${walletAddress.slice(-4)}` : "your wallet"}.
                No Hupper-held custody.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
